import { captureAction, clearUser } from '@smg-real-estate/browser-rum';
import { useEventListener } from '@vueuse/core';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import auth0Plugin from '@/auth/auth0Plugin';
import { useBillingContactsStore } from '@/stores/billingContacts';
import { useInsertionStore } from '@/stores/insertion';
import { useInvoicesStore } from '@/stores/invoice';
import { useMyAdsStore } from '@/stores/myAds';
import { usePaymentStore } from '@/stores/payment';
import { useUserStore } from '@/stores/user';

export function useSignOut() {
  const baseUrl = import.meta.env.VUE_APP_BASE_URL;

  const isLocalHost = !import.meta.env.SSR && window.location.hostname === 'localhost';

  const { locale } = useI18n();
  const insertionStore = useInsertionStore();
  const billingContactsStore = useBillingContactsStore();
  const invoicesStore = useInvoicesStore();
  const paymentStore = usePaymentStore();
  const myAdsStore = useMyAdsStore();
  const userStore = useUserStore();

  const channel = ref<BroadcastChannel>();
  onMounted(() => {
    channel.value = new BroadcastChannel('logout-sync');

    useEventListener(channel, 'message', (event: MessageEvent): void => {
      if (event.data === 'logout') {
        captureAction('Received logout message via BroadcastChannel', {
          channel: 'logout-sync',
          data: event.data,
          origin: event.origin,
          type: event.type,
        });
        void signOut(false);
      }
    });
  });

  /**
   * Clears our in-memory data and either redirects to the sign-out page (for active tab),
   * or reloads the page (for inactive tabs).
   *
   * @remarks
   * The auth0 logout function is only called for localhost.
   * In test & production environments, this is handled by the sign-out page.
   *
   * @param redirectToSignout - If true, redirects to the sign-out page afterwards.
   */
  async function signOut(redirectToSignout = true) {
    channel.value?.close();

    userStore.$reset();
    insertionStore.$reset();
    billingContactsStore.$reset();
    myAdsStore.$reset();
    invoicesStore.$reset();
    paymentStore.$reset();
    clearUser();

    if (isLocalHost) await auth0Plugin.logout();

    const signOutUrl = `${baseUrl}/${locale.value}/sign-out`;
    if (!import.meta.env.SSR) {
      if (redirectToSignout) window.location.assign(signOutUrl);
      else window.location.reload();
    }
  }

  return {
    signOut,
  };
}
